import { apiHelpers } from '@/services/api';
import type {
  Birthday,
  CreateBirthdayDto,
  UpdateBirthdayDto,
} from '@/types/birthday.types';

// API response types for endpoints that still use wrappers
interface BirthdaysThisMonthResponse {
  birthdays: Birthday[];
  month: string;
}

interface BirthdaysThisWeekResponse {
  birthdays: Birthday[];
  count: number;
}

// Birthday Service
export const birthdayService = {
  /**
   * Get all birthdays (public birthdays of all users)
   */
  async getAllBirthdays(): Promise<Birthday[]> {
    const response = await apiHelpers.get<Birthday[]>('/birthdays');
    return response ?? [];
  },

  /**
   * Get upcoming birthdays within specified days
   * @param days Number of days to look ahead (default: 30)
   */
  async getUpcomingBirthdays(days: number = 30): Promise<Birthday[]> {
    const response = await apiHelpers.get<Birthday[]>('/birthdays/upcoming', { days });
    return response ?? [];
  },

  /**
   * Get birthdays in the current month
   */
  async getBirthdaysThisMonth(): Promise<Birthday[]> {
    const response = await apiHelpers.get<BirthdaysThisMonthResponse>('/birthdays/this-month');
    return response.birthdays ?? [];
  },

  /**
   * Get birthdays this week
   */
  async getBirthdaysThisWeek(): Promise<Birthday[]> {
    const response = await apiHelpers.get<BirthdaysThisWeekResponse>('/birthdays/this-week');
    return response.birthdays ?? [];
  },

  /**
   * Get the current user's birthday
   */
  async getMyBirthday(): Promise<Birthday | null> {
    try {
      const response = await apiHelpers.get<Birthday | null>('/birthdays/me');
      return response ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Create or update the current user's birthday
   */
  async createOrUpdateBirthday(data: CreateBirthdayDto): Promise<Birthday> {
    return await apiHelpers.post<Birthday>('/birthdays', data);
  },

  /**
   * Update the current user's birthday
   */
  async updateBirthday(data: UpdateBirthdayDto): Promise<Birthday> {
    return await apiHelpers.patch<Birthday>('/birthdays/me', data);
  },

  /**
   * Delete the current user's birthday
   */
  async deleteBirthday(): Promise<void> {
    return apiHelpers.delete('/birthdays/me');
  },
};

export default birthdayService;
